import { useCallback, useEffect, useRef, useState } from 'react';
import {
  clearDeferredInstallPrompt,
  getDeferredInstallPrompt,
  subscribeInstallPrompt,
} from './deferredInstallPrompt';

const DISMISS_STORAGE_KEY = 'star123.installPrompt.dismissedAt';
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000;
const OPEN_EVENT = 'star123:open-download';
const TABLET_UA = /iPad|Tablet|PlayBook|Silk|Kindle|Nexus 7|Nexus 9|Nexus 10|SM-T\d|Lenovo TAB|Xoom|SCH-I800|GT-P/i;

function readDismissedAt() {
  try {
    const raw = window.localStorage.getItem(DISMISS_STORAGE_KEY);
    if (!raw) return 0;
    const value = Number(raw);
    return Number.isFinite(value) ? value : 0;
  } catch {
    return 0;
  }
}

function writeDismissedAt(timestamp) {
  try {
    window.localStorage.setItem(DISMISS_STORAGE_KEY, String(timestamp));
  } catch {
    // Ignore storage failures.
  }
}

function wasDismissedWithin24Hours() {
  const dismissedAt = readDismissedAt();
  if (!dismissedAt) return false;
  return Date.now() - dismissedAt < DISMISS_DURATION_MS;
}

export function isRunningAsInstalledApp() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const displayStandalone = window.matchMedia?.('(display-mode: standalone)')?.matches;
  const displayFullscreen = window.matchMedia?.('(display-mode: fullscreen)')?.matches;
  const iosStandalone = window.navigator.standalone === true;

  return Boolean(displayStandalone || displayFullscreen || iosStandalone);
}

export function isIosPhone() {
  if (typeof navigator === 'undefined') return false;
  return /iPhone/i.test(navigator.userAgent || '');
}

export function isMobilePhoneDevice() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const ua = navigator.userAgent || navigator.vendor || '';
  const uaData = navigator.userAgentData;

  const isIPad =
    /iPad/i.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  if (isIPad) return false;
  if (/Android/i.test(ua) && !/Mobile/i.test(ua)) return false;
  if (TABLET_UA.test(ua)) return false;
  if (uaData && typeof uaData.mobile === 'boolean' && uaData.mobile === false) {
    return false;
  }

  const isPhoneUa =
    /iPhone/i.test(ua) ||
    (/Android/i.test(ua) && /Mobile/i.test(ua)) ||
    /webOS|BlackBerry|IEMobile|Opera Mini|Windows Phone/i.test(ua);
  const isMobileHint = uaData?.mobile === true;

  if (!isPhoneUa && !isMobileHint) return false;

  const minSide = Math.min(window.innerWidth, window.innerHeight);
  if (minSide >= 600) return false;

  return true;
}

/** Open the Star123 download sheet from any CTA (Header / Home / etc.). */
export function openStar123Download(detail = {}) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail }));
}

export default function useMobileInstallPrompt() {
  const [isPhone, setIsPhone] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [openedManually, setOpenedManually] = useState(false);
  const [canNativeInstall, setCanNativeInstall] = useState(
    () => Boolean(getDeferredInstallPrompt())
  );

  const refreshDevice = useCallback(() => {
    const phone = isMobilePhoneDevice() && !isRunningAsInstalledApp();
    setIsPhone(phone);
    return phone;
  }, []);

  useEffect(() => {
    const phone = refreshDevice();
    if (!phone || wasDismissedWithin24Hours()) {
      return undefined;
    }

    const showTimer = window.setTimeout(() => {
      setOpenedManually(false);
      setIsVisible(true);
    }, 650);

    return () => window.clearTimeout(showTimer);
  }, [refreshDevice]);

  useEffect(() => {
    const onResize = () => {
      const phone = refreshDevice();
      if (!phone && !openedManually) {
        setIsVisible(false);
      }
    };

    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [refreshDevice, openedManually]);

  useEffect(() => {
    return subscribeInstallPrompt((event) => {
      setCanNativeInstall(Boolean(event));
    });
  }, []);

  useEffect(() => {
    const onOpen = () => {
      if (isRunningAsInstalledApp()) return;
      setOpenedManually(true);
      setIsVisible(true);
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  const dismiss = useCallback(() => {
    writeDismissedAt(Date.now());
    setOpenedManually(false);
    setIsVisible(false);
  }, []);

  const promptNativeInstall = useCallback(async () => {
    const installEvent = getDeferredInstallPrompt();
    if (!installEvent) return { available: false, outcome: 'unavailable' };

    try {
      await installEvent.prompt();
      const choice = await installEvent.userChoice;
      clearDeferredInstallPrompt();
      setCanNativeInstall(false);
      return { available: true, outcome: choice?.outcome || 'dismissed' };
    } catch {
      return { available: true, outcome: 'error' };
    }
  }, []);

  return {
    isPhone,
    isVisible,
    openedManually,
    canNativeInstall,
    isIos: isIosPhone(),
    dismiss,
    promptNativeInstall,
  };
}
